import React from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import useSWR from 'swr';

import useQueryInlineEdit from 'src/pages/QueryPages/hooks/useQueryInlineEdit';
import EditableCell from 'src/pages/QueryPages/components/EditableCell';
import EditFieldModal from 'src/pages/QueryPages/components/EditFieldModal';
import { QUERY_TABLE_VISIBILITY } from 'src/constants/config';
import { queryApi } from 'src/api/queryApi';
import { expertApi } from 'src/api/expertApi';
import { setTablePageSize } from 'src/store/slices/viewModeSlice';
import QueryStatus from 'src/pages/QueryPages/components/QueryStatus';
import UserSelect from 'src/pages/QueryPages/components/UserSelect';
import CompanySelect from 'src/pages/QueryPages/components/CompanySelect';
import ClaimButton from 'src/components/ClaimButton';
import { trimNameToThreeWords } from 'src/utils/string';
import { parseQueryNotes } from 'src/utils/queryNotesUtils';

const QueryTable = ({ queries, users, path, mutate, currentSearchParams }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tablePageSize = useSelector((state) => state.viewMode.tablePageSize) || 25;

  const [currentPage, setCurrentPage] = React.useState(0);

  const { editModal, handleEditValueChange, handleOpenEditModal, handleCloseEditModal, handleSaveFromModal } = useQueryInlineEdit(queries, mutate);

  const handlePageSizeChange = (newPageSize) => {
    dispatch(setTablePageSize(newPageSize));
  };

  const expertsFetcher = () => expertApi.getExperts();
  const { data: experts } = useSWR('experts', expertsFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000,
  });

  const expertIdToExpert = React.useMemo(() => {
    const map = new Map();
    (experts || []).forEach((expert) => {
      map.set(Number(expert.id), expert);
    });
    return map;
  }, [experts]);

  const getExpertInfo = React.useCallback(
    (row) => {
      if (row.status === 'draft') {
        const parsedNotes = parseQueryNotes(row.notes);
        if (parsedNotes.draftCompany) {
          return { name: '—', company: parsedNotes.draftCompany, avatar: null };
        }
      }

      if (!row || row.status === 'new' || row.status === 'draft') {
        return { name: '—', company: '—', avatar: null };
      }

      const preferredId = Number(row.expertsID);
      const primaryId = Number(row.expertID);
      const matchedExpert = expertIdToExpert.get(preferredId) || expertIdToExpert.get(primaryId);

      if (!matchedExpert) {
        return { name: 'Unknown Client', company: 'Unknown Company' };
      }

      const name = trimNameToThreeWords(matchedExpert.name);
      const company = matchedExpert.company || 'Unknown Company';
      const avatar = matchedExpert.imageURL || null;
      return { name, company, avatar };
    },
    [expertIdToExpert]
  );

  const userIdToUser = React.useMemo(() => {
    const map = new Map();
    (users || []).forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [users]);

  const rows = React.useMemo(() => {
    return (queries || []).map((row) => {
      const info = getExpertInfo(row);
      const parsedNotes = parseQueryNotes(row.notes);

      return {
        ...row,
        expertName: info.name,
        expertCompany: info.company,
        expertAvatar: info.avatar,
        dateWriting: parsedNotes.dateWriting,
        parsedNotes: parsedNotes.notes,
        draftCompany: parsedNotes.draftCompany,
      };
    });
  }, [queries, getExpertInfo]);

  const getVisibilityModel = React.useMemo(() => {
    const model = { ...QUERY_TABLE_VISIBILITY.DEFAULT };

    const presentStatuses = new Set((queries || []).map((q) => q.status));
    const allRuleColumns = new Set(Object.values(QUERY_TABLE_VISIBILITY.STATUS_RULES).flatMap((rules) => Object.keys(rules)));

    allRuleColumns.forEach((columnKey) => {
      let hideForAllStatuses = true;
      for (const status of presentStatuses) {
        const rules = QUERY_TABLE_VISIBILITY.STATUS_RULES[status];
        if (!rules || rules[columnKey] !== false) {
          hideForAllStatuses = false;
          break;
        }
      }
      if (hideForAllStatuses) {
        model[columnKey] = false;
      }
    });

    return model;
  }, [queries]);

  const handleDiscardQuery = React.useCallback(
    async (queryID) => {
      const response = await queryApi.updateQuery(queryID, 'discarded');
      if (response.status === 'Query Updated') {
        toast.success('Query discarded successfully');
        mutate();
      } else {
        toast.error('Failed to discard query');
      }
    },
    [mutate]
  );

  const createQueryPath = React.useCallback(
    (queryId) => {
      const basePath = path.replace(':queryID', queryId);
      return currentSearchParams ? `${basePath}?${currentSearchParams}` : basePath;
    },
    [path, currentSearchParams]
  );

  const formatDateTimeToInput = (date) => {
    if (!date) return '';
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const HH = String(date.getHours()).padStart(2, '0');
    const MM = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${HH}:${MM}`;
  };

  const columns = React.useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        minWidth: 80,
        flex: 0.5,
      },
      {
        field: 'dateWriting',
        headerName: 'Date writing',
        minWidth: 180,
        flex: 1,
        renderCell: (params) => {
          const currentDate = params.row.dateWriting ? new Date(params.row.dateWriting) : null;
          return (
            <EditableCell
              value={currentDate ? format(currentDate, 'MMM dd, yyyy') : ''}
              placeholder="No date"
              onClick={() => handleOpenEditModal(params.row.id, 'dateWriting', formatDateTimeToInput(currentDate), params.row.dateWriting)}
            />
          );
        },
      },
      {
        field: 'dueDate',
        headerName: 'Deadline',
        minWidth: 140,
        flex: 1,
        renderCell: (params) => {
          const currentDate = params.row.dueDate ? new Date(params.row.dueDate) : null;
          return (
            <EditableCell
              value={currentDate ? format(currentDate, 'MMM dd, yyyy') : ''}
              placeholder="No date"
              onClick={() => handleOpenEditModal(params.row.id, 'deadlineDate', currentDate ? format(currentDate, 'yyyy-MM-dd') : '')}
            />
          );
        },
      },
      {
        field: 'dueTime',
        headerName: 'Time',
        minWidth: 120,
        flex: 0.7,
        sortable: false,
        renderCell: (params) => {
          const currentDate = params.row.dueDate ? new Date(params.row.dueDate) : null;
          return (
            <EditableCell
              value={currentDate ? format(currentDate, 'HH:mm') : ''}
              placeholder="No time"
              onClick={() => handleOpenEditModal(params.row.id, 'deadlineTime', currentDate ? format(currentDate, 'HH:mm') : '')}
            />
          );
        },
      },
      {
        field: 'sentToExpertDate',
        headerName: 'Sent to client',
        minWidth: 125,
        flex: 0.8,
        valueGetter: (params) => (params.row.sentToExpertDate ? format(new Date(params.row.sentToExpertDate), 'MMM dd, yyyy') : 'No date'),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 180,
        flex: 1,
        renderCell: (params) => <QueryStatus query={params.row} mutate={mutate} />,
      },
      {
        field: 'claimedBy',
        headerName: 'Claimed by',
        minWidth: 180,
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ width: '100%' }}>
            <UserSelect users={users} selectedUserId={params.row.userClaimID} query={params.row} mutate={mutate} />
          </Box>
        ),
      },
      {
        field: 'company',
        headerName: 'Company',
        minWidth: 260,
        flex: 1.5,
        sortable: false,
        renderCell: (params) => {
          if (params.row.status === 'draft') {
            return (
              <Box sx={{ width: '100%' }}>
                <CompanySelect query={params.row} mutate={mutate} />
              </Box>
            );
          }
          return <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{params.row.expertCompany}</Box>;
        },
      },
      {
        field: 'expert',
        headerName: 'Expert',
        minWidth: 200,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Avatar src={params.row.expertAvatar} sx={{ width: 24, height: 24, flexShrink: 0, '& img': { objectFit: 'cover' } }}>
              {params.row.expertName.charAt(0)}
            </Avatar>
            {params.row.expertName}
          </Box>
        ),
      },
      {
        field: 'title',
        headerName: 'Title',
        minWidth: 500,
        flex: 3,
        renderCell: (params) => (
          <div
            style={{
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
            onClick={() => navigate(createQueryPath(params.row.id))}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: 'mediaOutlet',
        headerName: 'Media outlet',
        minWidth: 200,
        flex: 1.2,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', gap: 1 }}>
            <Avatar src={params.row.qwotedImage} sx={{ width: 24, height: 24, '& img': { objectFit: 'contain', backgroundColor: 'white' } }} />
            {params.value}
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 220,
        flex: 1.3,
        sortable: false,
        renderCell: (params) => {
          const claimedUser = userIdToUser.get(params.row.userClaimID);
          return (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <ClaimButton
                query={params.row}
                claimedUser={claimedUser}
                mutate={mutate}
                sx={{
                  display: 'flex',
                  minWidth: 'auto',
                  width: '100%',
                  '& .MuiButton-root': { padding: '4px 8px', minWidth: 0, whiteSpace: 'nowrap' },
                }}
                handleDiscardQuery={() => handleDiscardQuery(params.row.id)}
                hideAvatar
              />
            </Box>
          );
        },
      },
      {
        field: 'notes',
        headerName: 'Note',
        minWidth: 300,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <EditableCell
            value={params.row.parsedNotes || ''}
            placeholder="Click to add note..."
            onClick={() => handleOpenEditModal(params.row.id, 'notes', params.row.parsedNotes || '')}
          />
        ),
      },
    ],
    [userIdToUser, mutate, navigate, handleDiscardQuery, createQueryPath, handleOpenEditModal, users]
  );

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={{ pageSize: tablePageSize, page: currentPage }}
          onPaginationModelChange={(model) => {
            setCurrentPage(model.page);
            handlePageSizeChange(model.pageSize);
          }}
          disableSelectionOnClick
          autoHeight
          getRowId={(row) => row.id}
          isRowSelectable={() => false}
          columnVisibilityModel={getVisibilityModel}
          sx={{
            borderColor: 'divider',
            backgroundColor: 'white',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            '& .MuiDataGrid-cell': {
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              overflow: 'visible !important',
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: 2,
              borderColor: 'divider',
              backgroundColor: 'white',
              '& .MuiDataGrid-columnHeader': {
                py: 2,
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
              },
            },
            '& .MuiDataGrid-columnHeader': {
              borderRight: 1,
              borderColor: 'divider',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Box>

      <EditFieldModal
        editModal={editModal}
        onValueChange={handleEditValueChange}
        onClose={handleCloseEditModal}
        onSave={handleSaveFromModal}
        dateWritingInputType="datetime-local"
      />
    </>
  );
};

QueryTable.propTypes = {
  queries: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  path: PropTypes.string.isRequired,
  mutate: PropTypes.func.isRequired,
  currentSearchParams: PropTypes.string,
};

export default QueryTable;
